/**
 * The audio agent. Every sound is synthesized with Web Audio: no files to load or license.
 * Browsers only allow sound after a tap, so `unlock()` is called from the "Let's build!" button.
 */
export class Audio {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private noise!: AudioBuffer;
  muted = false;
  private hum?: { osc: OscillatorNode; osc2: OscillatorNode; filter: BiquadFilterNode; gain: GainNode };
  private buzz?: { osc: OscillatorNode; lfo: OscillatorNode; gain: GainNode };
  private sizzle?: { src: AudioBufferSourceNode; gain: GainNode };

  unlock() {
    if (this.ctx) {
      void this.ctx.resume();
      return;
    }
    const AC =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.muted ? 0 : 0.8;
    const comp = this.ctx.createDynamicsCompressor();
    comp.threshold.value = -14;
    comp.ratio.value = 4;
    this.master.connect(comp).connect(this.ctx.destination);
    this.noise = this.ctx.createBuffer(1, this.ctx.sampleRate, this.ctx.sampleRate);
    const d = this.noise.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  }

  get ready() {
    return !!this.ctx;
  }

  setMuted(m: boolean) {
    this.muted = m;
    if (this.ctx) this.master.gain.setTargetAtTime(m ? 0 : 0.8, this.ctx.currentTime, 0.02);
  }

  // ───────────────────────── One-shots ─────────────────────────
  private tone(
    freq: number,
    dur: number,
    type: OscillatorType = 'sine',
    vol = 0.3,
    slideTo?: number,
    delay = 0,
  ) {
    const c = this.ctx;
    if (!c) return;
    const t = c.currentTime + delay;
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    if (slideTo) o.frequency.exponentialRampToValueAtTime(slideTo, t + dur);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol, t + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(this.master);
    o.start(t);
    o.stop(t + dur + 0.02);
  }

  private burst(
    dur: number,
    freq: number,
    q = 1,
    vol = 0.4,
    type: BiquadFilterType = 'bandpass',
    delay = 0,
    sweepTo?: number,
  ) {
    const c = this.ctx;
    if (!c) return;
    const t = c.currentTime + delay;
    const src = c.createBufferSource();
    src.buffer = this.noise;
    const f = c.createBiquadFilter();
    f.type = type;
    f.frequency.setValueAtTime(freq, t);
    if (sweepTo) f.frequency.exponentialRampToValueAtTime(sweepTo, t + dur);
    f.Q.value = q;
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol, t + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(f).connect(g).connect(this.master);
    src.start(t, Math.random() * 0.5);
    src.stop(t + dur + 0.02);
  }

  /** The signature satisfying snap: a plastic click plus a tiny metal "tink". */
  snap() {
    this.burst(0.05, 3200, 1.2, 0.55);
    this.tone(2100, 0.06, 'triangle', 0.18, 1300);
    this.burst(0.04, 1400, 2, 0.35, 'bandpass', 0.03);
  }
  /** A soft tick for each stud a wire stretches over. Pitch rises a little each time. */
  private tickN = 0;
  tick() {
    this.tickN = (this.tickN + 1) % 12;
    this.tone(900 + this.tickN * 60, 0.03, 'triangle', 0.08);
  }
  /** Zzzip! Pulling a wire out of a snap. */
  zip() {
    this.tickN = 0;
    this.burst(0.12, 2600, 1.5, 0.25, 'bandpass', 0, 5200);
    this.tone(500, 0.1, 'sine', 0.12, 1100);
  }
  pickup() {
    this.tone(420, 0.12, 'sine', 0.22, 760);
  }
  drop() {
    this.tone(500, 0.1, 'sine', 0.18, 300);
  }
  invalid() {
    this.tone(220, 0.16, 'square', 0.07, 160);
    this.tone(180, 0.18, 'square', 0.06, 130, 0.09);
  }
  poof() {
    this.burst(0.25, 900, 0.7, 0.3, 'lowpass', 0, 200);
  }
  click() {
    this.burst(0.03, 4200, 3, 0.4);
    this.tone(1500, 0.03, 'square', 0.06);
  }
  pop() {
    this.tone(660, 0.08, 'sine', 0.2, 990);
  }
  lit() {
    this.tone(1320, 0.18, 'sine', 0.08);
    this.tone(1760, 0.22, 'sine', 0.06, undefined, 0.05);
  }
  launch() {
    this.burst(0.9, 400, 0.8, 0.5, 'bandpass', 0, 3500);
    this.tone(300, 0.7, 'sawtooth', 0.06, 1400);
  }
  boing() {
    this.tone(180, 0.35, 'sine', 0.35, 520);
    this.tone(520, 0.25, 'triangle', 0.08, 260, 0.08);
  }
  zap() {
    this.burst(0.35, 2500, 0.5, 0.5, 'highpass');
    this.tone(90, 0.3, 'sawtooth', 0.12, 50);
  }
  /** Ta-da! */
  success() {
    const notes = [523.25, 659.25, 783.99, 1046.5];
    notes.forEach((n, i) => this.tone(n, 0.35, 'triangle', 0.22, undefined, i * 0.09));
    this.tone(1046.5 * 1.5, 0.6, 'sine', 0.1, undefined, 0.36);
  }
  star(i: number) {
    this.tone(880 * (1 + i * 0.25), 0.25, 'triangle', 0.2);
  }

  // ───────────────────────── Continuous voices ─────────────────────────
  /** Motor whine. `omega` in rad/s (0 = off). */
  motor(omega: number) {
    const c = this.ctx;
    if (!c) return;
    const speed = Math.min(1, Math.abs(omega) / 60);
    if (!this.hum) {
      const osc = c.createOscillator();
      const osc2 = c.createOscillator();
      const filter = c.createBiquadFilter();
      const gain = c.createGain();
      osc.type = 'sawtooth';
      osc2.type = 'square';
      filter.type = 'lowpass';
      filter.Q.value = 4;
      gain.gain.value = 0;
      osc.connect(filter);
      osc2.connect(filter);
      filter.connect(gain).connect(this.master);
      osc.start();
      osc2.start();
      this.hum = { osc, osc2, filter, gain };
    }
    const t = c.currentTime;
    this.hum.osc.frequency.setTargetAtTime(60 + speed * 260, t, 0.05);
    this.hum.osc2.frequency.setTargetAtTime(30 + speed * 130, t, 0.05);
    this.hum.filter.frequency.setTargetAtTime(300 + speed * 1600, t, 0.05);
    this.hum.gain.gain.setTargetAtTime(speed > 0.02 ? 0.05 + speed * 0.07 : 0, t, 0.06);
  }

  /** Buzzer tone. `level` 0…1. */
  buzzer(level: number) {
    const c = this.ctx;
    if (!c) return;
    if (!this.buzz) {
      const osc = c.createOscillator();
      const lfo = c.createOscillator();
      const lfoGain = c.createGain();
      const gain = c.createGain();
      osc.type = 'square';
      osc.frequency.value = 1100;
      lfo.frequency.value = 9;
      lfoGain.gain.value = 40;
      lfo.connect(lfoGain).connect(osc.frequency);
      gain.gain.value = 0;
      osc.connect(gain).connect(this.master);
      osc.start();
      lfo.start();
      this.buzz = { osc, lfo, gain };
    }
    this.buzz.gain.gain.setTargetAtTime(level > 0 ? 0.035 + level * 0.04 : 0, c.currentTime, 0.015);
  }

  /** Short-circuit crackle. `level` 0…1. */
  short(level: number) {
    const c = this.ctx;
    if (!c) return;
    if (!this.sizzle) {
      const src = c.createBufferSource();
      src.buffer = this.noise;
      src.loop = true;
      const f = c.createBiquadFilter();
      f.type = 'highpass';
      f.frequency.value = 3000;
      const gain = c.createGain();
      gain.gain.value = 0;
      src.connect(f).connect(gain).connect(this.master);
      src.start();
      this.sizzle = { src, gain };
    }
    this.sizzle.gain.gain.setTargetAtTime(level * 0.12, c.currentTime, 0.05);
  }
}

/** The haptics agent: phone vibrations where supported (Android). Silently does nothing elsewhere (iOS). */
export const Haptics = {
  enabled: true,
  buzz(pattern: number | number[]) {
    if (!this.enabled) return;
    try {
      navigator.vibrate?.(pattern);
    } catch {
      /* not supported */
    }
  },
  snap() {
    this.buzz(12);
  },
  tick() {
    this.buzz(8);
  },
  invalid() {
    this.buzz([20, 40, 20]);
  },
  short() {
    this.buzz([60, 30, 60, 30, 120]);
  },
  launch() {
    this.buzz([30, 20, 60, 20, 140]);
  },
  success() {
    this.buzz([20, 60, 20, 60, 40, 60, 120]);
  },
};
